import React from 'react';

const PasswordStrength = ({ password }) => {
  if (!password) return null;

  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const strength = Object.values(checks).filter(Boolean).length;

  const getStrengthLabel = () => {
    if (strength <= 1) return 'Weak';
    if (strength === 2) return 'Fair';
    if (strength === 3) return 'Good';
    return 'Strong';
  };

  const getStrengthColor = () => {
    if (strength <= 1) return '#dc2626';
    if (strength === 2) return '#f97316';
    if (strength === 3) return '#eab308';
    return '#16a34a';
  };

  const label = getStrengthLabel();
  const color = getStrengthColor();
  const percentage = (strength / 4) * 100;

  const requirements = [
    { key: 'length', label: 'At least 8 characters', met: checks.length },
    { key: 'uppercase', label: 'Contains uppercase letter', met: checks.uppercase },
    { key: 'lowercase', label: 'Contains lowercase letter', met: checks.lowercase },
    { key: 'number', label: 'Contains a number', met: checks.number },
  ];

  const styles = {
    container: {
      marginTop: '0.5rem',
      marginBottom: '0.25rem',
    },
    barWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.5rem',
    },
    barTrack: {
      flex: 1,
      height: '6px',
      backgroundColor: '#e5e7eb',
      borderRadius: '3px',
      overflow: 'hidden',
    },
    barFill: {
      height: '100%',
      width: `${percentage}%`,
      backgroundColor: color,
      borderRadius: '3px',
      transition: 'width 0.3s ease, background-color 0.3s ease',
    },
    label: {
      fontSize: '0.8rem',
      fontWeight: '600',
      color: color,
      minWidth: '48px',
      textAlign: 'right',
      transition: 'color 0.3s ease',
    },
    requirementsList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.25rem 1rem',
    },
    requirementItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      fontSize: '0.8rem',
      lineHeight: '1.4',
    },
    icon: (met) => ({
      flexShrink: 0,
      fontSize: '0.85rem',
      color: met ? '#16a34a' : '#9ca3af',
    }),
    text: (met) => ({
      color: met ? '#374151' : '#9ca3af',
      transition: 'color 0.2s ease',
    }),
  };

  return (
    <div style={styles.container}>
      <div style={styles.barWrapper}>
        <div style={styles.barTrack}>
          <div style={styles.barFill} />
        </div>
        <span style={styles.label}>{label}</span>
      </div>
      <ul style={styles.requirementsList}>
        {requirements.map((req) => (
          <li key={req.key} style={styles.requirementItem}>
            <span style={styles.icon(req.met)}>
              {req.met ? '\u2713' : '\u2717'}
            </span>
            <span style={styles.text(req.met)}>{req.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PasswordStrength;
